'use client';

import axios from 'axios';

// Cache for API responses
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 60000; // 1 minute

export interface YahooFinanceQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
  volume?: number;
  timestamp: number;
}

export const yahooFinanceService = {
  /**
   * Fetch real-time quote for a single symbol
   */
  async getQuote(symbol: string): Promise<YahooFinanceQuote | null> {
    const cacheKey = `quote_${symbol}`;
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await axios.get(`/api/yahoo-finance/quote`, {
        params: { symbol },
        timeout: 10000,
      });

      const quote = response.data as YahooFinanceQuote;
      
      if (!quote || !quote.price) {
        console.error('Invalid response from Yahoo Finance API:', response.data);
        return null;
      }

      cache.set(cacheKey, { data: quote, timestamp: Date.now() });
      return quote;
    } catch (error) {
      console.error(`Yahoo Finance API error for ${symbol}:`, error);
      return null;
    }
  },

  /**
   * Fetch quotes for multiple symbols in parallel
   */
  async getMultipleQuotes(symbols: string[]): Promise<Map<string, YahooFinanceQuote>> {
    const results = new Map<string, YahooFinanceQuote>();
    
    try {
      const response = await axios.post('/api/yahoo-finance/quotes', 
        { symbols },
        { timeout: 15000 }
      );

      const quotes = response.data as YahooFinanceQuote[];
      quotes.forEach((quote) => {
        if (quote && quote.price) {
          results.set(quote.symbol, quote);
        }
      });
    } catch (error) {
      console.error('Yahoo Finance multiple quotes error:', error);
    }

    return results;
  },

  /**
   * Search for symbols
   */
  async searchSymbols(query: string) {
    try {
      const response = await axios.get('/api/yahoo-finance/search', {
        params: { query },
        timeout: 10000,
      });
      return response.data;
    } catch (error) {
      console.error('Yahoo Finance symbol search error:', error);
      return null;
    }
  },

  /**
   * Clear cache
   */
  clearCache() {
    cache.clear();
  },
};